import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../SafeIcon';
import supabase from '../../../supabase/supabase';
import ProductModal from '../../store/ProductModal';
import { useCart } from '../../../context/CartContext';
import { getBadgeStyle } from '../../dashboard/store/ProductsPanel';

const { FiHeart, FiShoppingCart, FiPackage } = FiIcons;

const ProductCard = ({ product, showSaleBadge, onOpenDetails }) => {
  const [wishlisted, setWishlisted] = useState(false);
  const { addToCart } = useCart();

  const price = Number(product.price || 0);
  const comparePrice = Number(product.compare_at_price || 0);
  const isOnSale = showSaleBadge && !product.badge && (product.on_sale || (comparePrice > 0 && comparePrice > price));
  const discountPct = isOnSale && comparePrice > price
    ? Math.round(((comparePrice - price) / comparePrice) * 100)
    : null;

  const coverImg = (Array.isArray(product.images) && product.images.length > 0)
    ? product.images[0]
    : product.image_url || '';

  const badgeStyle = product.badge ? getBadgeStyle(product.badge) : null;

  return (
    <div
      onClick={() => onOpenDetails(product)}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 group relative flex flex-col cursor-pointer hover:shadow-lg hover:border-teal-100 transition-all duration-300"
    >
      <div className="relative overflow-hidden bg-gray-50 aspect-square">
        {coverImg ? (
          <img src={coverImg} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <SafeIcon icon={FiPackage} className="w-16 h-16 text-gray-200" />
          </div>
        )}

        {badgeStyle && (
          <div
            className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg"
            style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text }}
          >
            {product.badge}
          </div>
        )}

        {isOnSale && !badgeStyle && (
          <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow">
            {discountPct ? `${discountPct}% OFF` : 'SALE'}
          </div>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); setWishlisted(w => !w); }}
          className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center transition-all hover:scale-110"
        >
          <SafeIcon icon={FiHeart} className={`w-4 h-4 transition-colors ${wishlisted ? 'text-red-500' : 'text-gray-400'}`}
            style={wishlisted ? { fill: 'currentColor' } : {}} />
        </button>
      </div>

      <div className="p-5 flex flex-col flex-1 relative">
        {product.category && (
          <span className="text-[10px] font-black uppercase tracking-widest text-teal-500 mb-1.5">
            {product.category.split(',')[0]}
          </span>
        )}
        <h3 className="font-bold text-gray-900 text-base leading-tight mb-1 line-clamp-2">{product.name}</h3>
        <div className="flex items-baseline space-x-2 mt-2 mb-4">
          <span className="text-xl font-black text-gray-900">R{price.toFixed(2)}</span>
          {isOnSale && comparePrice > price && (
            <span className="text-sm text-gray-400 line-through font-medium">R{comparePrice.toFixed(2)}</span>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); addToCart(product); }}
          disabled={product.stock <= 0}
          className="w-full mt-auto py-3 bg-[#111827] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-colors text-sm flex items-center justify-center space-x-2 shadow-sm"
        >
          <SafeIcon icon={FiShoppingCart} className="w-4 h-4" />
          <span>{product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}</span>
        </button>
      </div>
    </div>
  );
};

const ProductsWidget = ({ styles = {}, onConfigure, projectId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const category = styles.productCategory || '';
  const sortBy = styles.sortBy || 'newest';
  const columns = styles.columns || 3;
  const mobileColumns = styles.mobileColumns || 1;
  const showSaleBadge = styles.showSaleBadge !== false;
  const limit = styles.limit || 6;
  const fullWidth = styles.fullWidth || false;

  useEffect(() => { fetchProducts(); }, [category, sortBy, limit, projectId]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('store_products_1772955957776')
        .select('*')
        .eq('is_active', true);

      if (projectId) query = query.eq('project_id', projectId);
      if (category) query = query.ilike('category', `%${category}%`);

      if (sortBy === 'price_asc') {
        query = query.order('price', { ascending: true });
      } else if (sortBy === 'price_desc') {
        query = query.order('price', { ascending: false });
      } else if (sortBy === 'alphabetical') {
        query = query.order('name', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.limit(limit);
      
      const { data, error } = await query;
      if (!error) setProducts(data || []);
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  };

  const getColumnClass = () => {
    const desktopCols = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' }[columns] || 'lg:grid-cols-3';
    const mobileCols = { 1: 'grid-cols-1', 2: 'grid-cols-2' }[mobileColumns] || 'grid-cols-1';
    return `${mobileCols} md:grid-cols-2 ${desktopCols}`;
  };

  if (!category && !styles.showAll) {
    return (
      <div onClick={onConfigure}
        className="w-full border-2 border-dashed border-blue-300 bg-blue-50/50 rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
          <SafeIcon icon={FiPackage} className="w-6 h-6 text-blue-500" />
        </div>
        <p className="font-black text-blue-600 text-sm uppercase tracking-widest">Products Widget</p>
        <p className="text-xs text-blue-400 mt-1">Click to select a product category</p>
      </div>
    );
  }

  const gridContent = (
    <>
      {loading ? (
        <div className="flex justify-center py-20 col-span-full">
          <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-gray-400 col-span-full">
          <SafeIcon icon={FiPackage} className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-base font-medium">No products found in this category.</p>
          {projectId && <p className="text-sm mt-1">Add products to this project in your store.</p>}
        </div>
      ) : (
        products.map(p => (
          <ProductCard key={p.id} product={p} showSaleBadge={showSaleBadge} onOpenDetails={setSelectedProduct} />
        ))
      )}
    </>
  );

  return (
    <div className="w-full py-4 relative">
      {fullWidth ? (
        <div className={`grid ${getColumnClass()} gap-6 md:gap-8`}>{gridContent}</div>
      ) : (
        <div className="max-w-7xl mx-auto px-4">
          <div className={`grid ${getColumnClass()} gap-6 md:gap-8`}>{gridContent}</div>
        </div>
      )}
      <AnimatePresence>
        {selectedProduct && (
          <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsWidget;